import React, { useState, useEffect, useCallback } from 'react';
import { graphBuilderApi } from '../../../services/api';
import { aamlDomains } from '../utils/nodeTemplates';
import AggregateDecisionCard from './AggregateDecisionCard';
import './ExecutionCommon.css';

function ExecutionRunForm({
  processDefinition,
  execution,
  onStart,
  onStep,
  onPause,
  onResume,
  onCancel,
  executionMode,
  isStarting = false,
}) {
  const [inputs, setInputs] = useState({});
  const [errors, setErrors] = useState({});
  const [reportStatus, setReportStatus] = useState(null); // { available, generating }

  const pollReportStatus = useCallback(async () => {
    if (!execution?.execution_id) return;
    try {
      const status = await graphBuilderApi.getReportStatus(execution.execution_id);
      setReportStatus(status);
    } catch {
      // silently ignore
    }
  }, [execution?.execution_id]);

  useEffect(() => {
    if (execution?.status !== 'completed' && execution?.status !== 'failed') {
      setReportStatus(null);
      return;
    }
    if (execution?.report_available) {
      setReportStatus({ available: true });
      return;
    }
    pollReportStatus();
    const interval = setInterval(pollReportStatus, 5000);
    return () => clearInterval(interval);
  }, [execution?.status, execution?.report_available, pollReportStatus]);

  // Get all required inputs from entry nodes
  const getRequiredInputs = () => {
    console.log('getRequiredInputs - processDefinition:', processDefinition);
    
    // Support both nested (definition.nodes) and flat (nodes) structure
    const nodes = processDefinition?.definition?.nodes || processDefinition?.nodes;
    console.log('getRequiredInputs - nodes:', nodes);
    
    if (!nodes) return [];
    
    const synapses = processDefinition?.definition?.synapses || processDefinition?.synapses || [];
    console.log('getRequiredInputs - synapses:', synapses);
    
    // Find entry nodes (nodes with no incoming synapses)
    const targetNodes = new Set(synapses.map(s => s.target_node));
    console.log('getRequiredInputs - targetNodes:', [...targetNodes]);
    
    const entryNodes = nodes.filter(n => !targetNodes.has(n.node_id));
    console.log('getRequiredInputs - entryNodes:', entryNodes);
    
    // Collect every entry-node input (required + optional). Optional
    // inputs are rendered too so analysts can supply a case_id or
    // override the default domain on the categorical reasoner.
    const allInputs = [];
    entryNodes.forEach(node => {
      console.log('getRequiredInputs - processing node:', node.name, 'inputs:', node.inputs);
      (node.inputs || []).forEach(input => {
        if (input.required && input.default) {
          // Required input with a default doesn't need analyst attention.
          return;
        }
        allInputs.push({
          ...input,
          nodeId: node.node_id,
          nodeName: node.name,
        });
      });
    });
    
    console.log('getRequiredInputs - allInputs:', allInputs);
    return allInputs;
  };

  const requiredInputs = getRequiredInputs();

  // Initialize inputs with defaults
  useEffect(() => {
    const defaultInputs = {};
    requiredInputs.forEach(input => {
      if (input.default) {
        defaultInputs[input.name] = input.default;
      }
    });
    setInputs(defaultInputs);
  }, [processDefinition]);

  // Handle input change
  const handleInputChange = (name, value) => {
    setInputs(prev => ({ ...prev, [name]: value }));
    // Clear error for this input
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate inputs
  const validateInputs = () => {
    const newErrors = {};
    requiredInputs.forEach(input => {
      const value = inputs[input.name];
      // Check if value exists and is not empty
      const isEmpty = value === undefined || value === null || 
        (typeof value === 'string' && !value.trim());
      
      if (input.required && isEmpty) {
        newErrors[input.name] = 'This field is required';
      }
    });
    console.log('Validation newErrors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle start
  const handleStart = () => {
    console.log('handleStart called');
    console.log('requiredInputs:', requiredInputs);
    console.log('inputs:', inputs);
    
    if (!validateInputs()) {
      console.log('Validation failed');
      return;
    }
    
    // Parse JSON inputs before sending
    const parsedInputs = {};
    const parseErrors = {};
    
    for (const input of requiredInputs) {
      const value = inputs[input.name];
      if (input.type === 'json' && value) {
        try {
          parsedInputs[input.name] = JSON.parse(value);
        } catch (e) {
          console.error('Failed to parse JSON for', input.name, e);
          parseErrors[input.name] = 'Invalid JSON format';
        }
      } else if (value !== undefined) {
        parsedInputs[input.name] = value;
      }
    }
    
    if (Object.keys(parseErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...parseErrors }));
      console.log('JSON parse errors:', parseErrors);
      return;
    }
    
    console.log('Validation passed, calling onStart with:', parsedInputs);
    onStart(parsedInputs);
  };

  // Get current execution status
  const status = execution?.status;
  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const isPending = status === 'pending';
  const isComplete = ['completed', 'failed', 'cancelled'].includes(status);
  
  console.log('ExecutionRunForm render - execution:', execution, 'status:', status, 'isComplete:', isComplete);

  // Calculate progress info
  const completedCount = execution?.completed_nodes?.length || 0;
  const activeCount = execution?.active_nodes?.length || 0;
  const pendingCount = execution?.pending_nodes?.length || 0;
  const totalNodes = (processDefinition?.definition?.nodes || processDefinition?.nodes)?.length || 0;
  const progress = execution?.progress || 0;

  return (
    <div className="execution-run-form">
      <div className="form-header">
        <h3>Execution</h3>
        {execution?.execution_id && (
          <span className="execution-id" title={execution.execution_id}>
            ID: {execution.execution_id.substring(0, 8)}...
          </span>
        )}
      </div>

      {/* Progress Section */}
      {execution && (
        <div className="progress-section">
          <div className="progress-bar-container">
            <div 
              className="progress-bar" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="progress-text">
            {progress.toFixed(1)}% Complete
          </div>
          <div className="progress-stats">
            <span className="stat completed">{completedCount} completed</span>
            <span className="stat active">{activeCount} active</span>
            <span className="stat pending">{pendingCount} pending</span>
          </div>
        </div>
      )}

      {/* Control Buttons */}
      <div className="control-buttons">
        {!execution || isComplete ? (
          <button 
            className="btn-primary run-btn"
            onClick={handleStart}
            disabled={isStarting}
          >
            {isStarting ? '⏳ Starting...' : '▶ Run Process'}
          </button>
        ) : (
          <>
            {executionMode === 'step' && (isRunning || isPending || isPaused) && (
              <button 
                className="btn-secondary"
                onClick={onStep}
              >
                ⏭ Step
              </button>
            )}
            {isRunning && (
              <button 
                className="btn-secondary"
                onClick={onPause}
              >
                ⏸ Pause
              </button>
            )}
            {isPaused && (
              <button 
                className="btn-secondary"
                onClick={onResume}
              >
                ▶ Resume
              </button>
            )}
            {(isRunning || isPaused || isPending) && (
              <button 
                className="btn-danger"
                onClick={onCancel}
              >
                ⏹ Cancel
              </button>
            )}
          </>
        )}
      </div>

      {/* Inputs Section */}
      {(!execution || isComplete) && (
        <div className="inputs-section">
          <h4>Inputs</h4>
          {requiredInputs.length === 0 ? (
            <p className="no-inputs">No inputs required for this process</p>
          ) : (
            <div className="inputs-list">
              {requiredInputs.map((input) => (
                <div key={input.name} className="input-field">
                  <label>
                    {input.name}
                    {input.required && <span className="required">*</span>}
                  </label>
                  {input.description && (
                    <span className="input-description">{input.description}</span>
                  )}
                  {(() => {
                    const isLongString = input.type === 'long_string'
                      || input.name === 'case_text'
                      || input.name === 'narrative'
                      || input.name === 'raw_text';
                    const isDomain = input.type === 'domain' || input.name === 'domain';

                    if (input.type === 'json') {
                      return (
                        <textarea
                          value={inputs[input.name] || ''}
                          onChange={(e) => handleInputChange(input.name, e.target.value)}
                          placeholder={input.default || 'Enter JSON...'}
                          rows={4}
                          className={errors[input.name] ? 'error' : ''}
                        />
                      );
                    }
                    if (isLongString) {
                      return (
                        <textarea
                          value={inputs[input.name] || ''}
                          onChange={(e) => handleInputChange(input.name, e.target.value)}
                          placeholder={input.default || `Paste the ${input.name} narrative...`}
                          rows={10}
                          className={`long-string-input ${errors[input.name] ? 'error' : ''}`}
                        />
                      );
                    }
                    if (isDomain) {
                      return (
                        <select
                          value={inputs[input.name] || input.default || 'AML'}
                          onChange={(e) => handleInputChange(input.name, e.target.value)}
                          className={errors[input.name] ? 'error' : ''}
                        >
                          {aamlDomains.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </select>
                      );
                    }
                    return (
                      <input
                        type={input.type === 'number' ? 'number' : 'text'}
                        value={inputs[input.name] || ''}
                        onChange={(e) => handleInputChange(input.name, e.target.value)}
                        placeholder={input.default || `Enter ${input.name}...`}
                        className={errors[input.name] ? 'error' : ''}
                      />
                    );
                  })()}
                  {errors[input.name] && (
                    <span className="error-message">{errors[input.name]}</span>
                  )}
                  <span className="input-meta">
                    From: {input.nodeName} | Type: {input.type}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Execution Context */}
      {execution?.context && Object.keys(execution.context).length > 0 && (
        <div className="context-section">
          <h4>Execution Context</h4>
          <div className="context-viewer">
            <pre>{JSON.stringify(execution.context, null, 2)}</pre>
          </div>
        </div>
      )}

      {/* Categorical reasoner -- aggregate decision card (when present) */}
      {execution?.final_outputs?.aggregate_decision && (
        <div className="outputs-section">
          <h4>Categorical Decision</h4>
          <AggregateDecisionCard
            report={{
              aggregate_decision: execution.final_outputs.aggregate_decision,
              aggregate_score: execution.final_outputs.aggregate_score,
              decision: execution.final_outputs.decision,
              score: execution.final_outputs.score,
              total_packs_fired: execution.final_outputs.total_packs_fired,
              total_packs_loaded: execution.final_outputs.total_packs_loaded,
              total_instances_fired: execution.final_outputs.total_instances_fired,
              fired_pack_ids: execution.final_outputs.fired_pack_ids,
              violations: execution.final_outputs.violations_report?.violations,
              aggregate_rule_trace: execution.final_outputs.violations_report?.aggregate_rule_trace,
            }}
            title="Aggregate decision (final_outputs)"
          />
          {execution.final_outputs.violations_report && (
            <button
              className="btn-secondary"
              type="button"
              onClick={() => {
                const blob = new Blob(
                  [JSON.stringify(execution.final_outputs.violations_report, null, 2)],
                  { type: 'application/json' },
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `violations_report_${execution.execution_id || 'run'}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              style={{ marginTop: '8px' }}
            >
              ⬇ Download violations_report.json
            </button>
          )}
        </div>
      )}

      {/* Final Outputs */}
      {execution?.final_outputs && Object.keys(execution.final_outputs).length > 0 && (
        <div className="outputs-section">
          <h4>Final Outputs</h4>
          <div className="outputs-viewer">
            <pre>{JSON.stringify(execution.final_outputs, null, 2)}</pre>
          </div>
        </div>
      )}

      {/* Report Download */}
      {(execution?.status === 'completed' || execution?.status === 'failed') && (
        <div className="report-download-section">
          {reportStatus?.available ? (
            <a
              href={graphBuilderApi.getReportDownloadUrl(execution.execution_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-report-download"
            >
              <span className="report-icon">&#128196;</span> Download Execution Report (PDF)
            </a>
          ) : reportStatus?.generating ? (
            <div className="report-generating">
              <span className="spinner-small" /> Generating report...
            </div>
          ) : (
            <div className="report-generating">
              <span className="spinner-small" /> Preparing report...
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {execution?.error && (
        <div className="execution-error">
          <h4>Execution Error</h4>
          <p>{execution.error}</p>
        </div>
      )}
    </div>
  );
}

export default ExecutionRunForm;
